import type { Company } from '../../types';
import type { VinculoEstipulanteItem } from '../../types/estipulanteVinculoType';
import type { VinculoNegocio } from '../../types/apiType';
import type { UserPoliciesListResponse } from '../../types/serviceType';

/** Policy placeholder in vinculo — temporary flow until removal. */
export const PLACEHOLDER_APOLICE_NUMERO = '00000000';

export const PLACEHOLDER_BACKOFFICE_SESSION_ROLE = 'BACKOFFICE';

export function normalizeApoliceDigits(numeroApolice: string | number | null | undefined): string {
  return String(numeroApolice ?? '').replace(/\D/g, '');
}

export function isPlaceholderApoliceNumber(numeroApolice: string | number | null | undefined): boolean {
  return normalizeApoliceDigits(numeroApolice) === PLACEHOLDER_APOLICE_NUMERO;
}

export function isPlaceholderApoliceVinculo(item: VinculoEstipulanteItem): boolean {
  return isPlaceholderApoliceNumber(item.parsed.numeroApolice);
}

export function extractPolicyRowsFromListarResponse(
  listRes: UserPoliciesListResponse,
): NonNullable<UserPoliciesListResponse[number]['retorno']> {
  return listRes
    .flatMap((group) => group.retorno ?? [])
    .filter((row): row is NonNullable<typeof row> => row != null);
}

/** HTTP 200 com `return` vazio — without policy data in listar-apol-perf-usuario. */
export function isListarApolPerfUsuarioWithoutPolicyData(listRes: UserPoliciesListResponse): boolean {
  return extractPolicyRowsFromListarResponse(listRes).length === 0;
}

export function shouldUsePlaceholderApoliceBackofficeFlow(
  item: VinculoEstipulanteItem,
  listRes: UserPoliciesListResponse,
): boolean {
  return isPlaceholderApoliceVinculo(item) && isListarApolPerfUsuarioWithoutPolicyData(listRes);
}

/**
 * Mantém ramo do último dígito da chave legado (3|4|5) para o sidebar,
 * sem apólice confirmada (`numeroApolice` vazio).
 */
export function buildPlaceholderBackofficeCompanySnapshot(item: VinculoEstipulanteItem): Company {
  const v = item.vinculo;
  const papel = v.representacao?.papel;

  return {
    id: v.idVinculoNegocio ?? '',
    policySelection: {
      documento: item.parsed.documento ?? item.parsed.cnpj,
      tipoDocumento: item.parsed.tipoDocumento ?? 'cnpj',
      cnpj: item.parsed.documento ?? item.parsed.cnpj,
      numeroApolice: '',
      ramoCodigo: item.parsed.ramoCodigo,
      ramoNome: item.parsed.ramoNome,
      papelId: papel?.id ?? '',
      papelDescricao: papel?.descricao ?? '',
      chaveLegado: v.representacao?.chaveLegado ?? '',
    },
  };
}

export interface ApplyPlaceholderApoliceBackofficeEntryParams {
  item: VinculoEstipulanteItem;
  setSessionRole: (role: string | null) => void;
  setSelectedCompany: (company: Company | null) => void;
  setSelectedVinculo: (vinculo: VinculoNegocio | null) => void;
}

/** Entry as BACKOFFICE without confirmed policy, subestipulantes or Vida protocol. */
export function applyPlaceholderApoliceBackofficeEntry({
  item,
  setSessionRole,
  setSelectedCompany,
  setSelectedVinculo,
}: ApplyPlaceholderApoliceBackofficeEntryParams): void {
  setSelectedVinculo(null);
  setSelectedCompany(buildPlaceholderBackofficeCompanySnapshot(item));
  setSessionRole(PLACEHOLDER_BACKOFFICE_SESSION_ROLE);
}
